import * as React from 'react';
import Markdown from 'react-markdown';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { LucideIcon } from './LucideIcon';
import type { Assistant as Interface } from '../../design/components';
import { AdaIcon } from '../shared/AdaIcon';

const TypingIndicator = () => (
  <div className="flex gap-1 items-center">
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce"
        style={{ animationDelay: `${i * 0.2}s`, animationDuration: '1.4s' }}
      />
    ))}
  </div>
);

const preprocessSteps = (content: string): string => {
  const allSteps: string[] = [];
  const processed = content.replace(/```steps\s*([\s\S]*?)```/g, (_, stepsContent) => {
    let steps = stepsContent.trim().split('\n').filter(Boolean);
    if (steps.length === 1 && steps[0].length > 50) {
      steps = steps[0].split(/(?<=[a-z.!?])\s*(?=[A-Z][a-z]+(?:ed|ing|d)\s)/).filter(Boolean);
    }
    allSteps.push(...steps);
    return '{{STEPS_PLACEHOLDER}}';
  });

  if (allSteps.length === 0) return content;

  const uniqueSteps = [...new Set(allSteps)];

  let first = true;
  return processed.replace(/\{\{STEPS_PLACEHOLDER\}\}/g, () => {
    if (first) {
      first = false;
      return '\n\n```steps\n' + uniqueSteps.join('\n') + '\n```\n\n';
    }
    return '';
  });
};

const CollapsibleSteps = ({ children }: { children: string }) => {
  const [expanded, setExpanded] = React.useState(false);
  const steps = children.trim().split('\n').filter(Boolean);
  const stepCount = steps.length;

  return (
    <div className="my-1">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer bg-transparent border-none p-0"
      >
        <LucideIcon name={expanded ? 'ChevronUp' : 'ChevronDown'} size="small" />
        {stepCount} {stepCount === 1 ? 'step' : 'steps'} completed
      </button>
      {expanded && (
        <div className="pl-5 pt-1 text-xs text-muted-foreground">
          {steps.map((step, i) => (
            <div key={i} className="flex gap-1">
              <span>•</span>
              <span>{step}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const Assistant: React.FC<Interface> = ({ state, onSend, disabled }) => {
  const [messages] = state || [[], () => {}];
  const [input, setInput] = React.useState('');
  const [files, setFiles] = React.useState<File[]>([]);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if ((!input.trim() && files.length === 0) || disabled) return;
    const content = input.trim() || `Attached ${files.length} file${files.length === 1 ? '' : 's'}`;
    const sendFiles = files.length > 0 ? files : undefined;
    setInput('');
    setFiles([]);
    const unused = await onSend?.(content, sendFiles);
    if (unused?.length) setFiles(unused);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files!)]);
    }
    e.target.value = '';
  };

  const [dragOver, setDragOver] = React.useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files).filter(f =>
      f.type.startsWith('image/') || f.type === 'application/pdf'
    );
    if (dropped.length > 0) setFiles(prev => [...prev, ...dropped]);
  };

  return (
    <div
      className={cn('flex flex-col h-full bg-secondary/30 relative', dragOver && 'ring-2 ring-primary ring-inset')}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={(e) => { if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false); }}
      onDrop={handleDrop}
    >
      {dragOver && (
        <div className="absolute inset-0 z-10 bg-primary/5 border-2 border-dashed border-primary rounded flex items-center justify-center pointer-events-none">
          <span className="text-sm text-primary">Drop files here</span>
        </div>
      )}
      {/* Header */}
      <div className="px-4 py-3 border-b bg-background flex items-center gap-2">
        <AdaIcon size={20} working={disabled} />
        <span className="text-sm font-medium">Ada</span>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 flex flex-col gap-3">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}
            >
              <div
                className={cn(
                  'py-2.5 px-3.5 max-w-[85%] text-sm leading-relaxed',
                  msg.error
                    ? 'bg-destructive/10 text-destructive rounded-2xl'
                    : msg.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-sm'
                    : 'bg-background shadow-sm rounded-2xl rounded-bl-sm',
                  '[&_p]:m-0 [&_p]:mb-2 [&_p:last-child]:mb-0',
                  '[&_ul]:m-0 [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:m-0 [&_ol]:pl-5 [&_ol]:mb-2',
                  '[&_li]:mb-0.5',
                  '[&_code]:bg-black/10 [&_code]:px-1 [&_code]:rounded [&_code]:font-mono [&_code]:text-[0.85em]',
                  '[&_pre]:bg-black/5 [&_pre]:p-2 [&_pre]:rounded [&_pre]:overflow-auto',
                  msg.role === 'user' && '[&_code]:bg-white/30'
                )}
              >
                {msg.role === 'user' ? (
                  <>
                    {msg.files && msg.files.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-1.5">
                        {msg.files.map((f, i) => (
                          <span key={i} className="text-xs bg-white/20 rounded px-1.5 py-0.5">{f.name}</span>
                        ))}
                      </div>
                    )}
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </>
                ) : (
                  <>
                    {msg.content && (
                      <Markdown
                        components={{
                          code({ className, children }) {
                            if (className === 'language-steps') {
                              return <CollapsibleSteps>{String(children)}</CollapsibleSteps>;
                            }
                            return <code className={className}>{children}</code>;
                          },
                          pre({ children }) {
                            const child = React.Children.toArray(children)[0];
                            if (React.isValidElement(child) && (child.props as { className?: string })?.className === 'language-steps') {
                              return <>{children}</>;
                            }
                            return <pre>{children}</pre>;
                          },
                        }}
                      >
                        {preprocessSteps(msg.content)}
                      </Markdown>
                    )}
                    {(msg.streaming || !msg.content) && <TypingIndicator />}
                  </>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-3 bg-background border-t">
        {files.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {files.map((f, i) => (
              <span key={i} className="text-xs bg-muted rounded px-2 py-0.5 inline-flex items-center gap-1">
                {f.name}
                <span onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="cursor-pointer font-bold">&times;</span>
              </span>
            ))}
          </div>
        )}
        <div className="relative flex items-center gap-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="shrink-0"
          >
            <LucideIcon name="Paperclip" size="small" />
          </Button>
          <Input
            placeholder="Type a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className="pr-12"
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSend}
            disabled={(!input.trim() && files.length === 0) || disabled}
            className="absolute right-1"
          >
            <LucideIcon name="Send" size="small" />
          </Button>
        </div>
      </div>
    </div>
  );
};
